export const DEFENSE_PROTOCOL_VERSION = 1 as const;

export type MultiplayerMode = "coop" | "head-to-head";
export type LobbyVisibility = "private" | "public";
export type ConnectionState =
  | "idle"
  | "authenticating"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "error";

export interface LobbyPlayer {
  readonly playerId: string;
  readonly displayName: string;
  readonly ready: boolean;
  readonly connected: boolean;
  readonly slot: number;
}

export interface LobbyState {
  readonly lobbyId: string;
  readonly code: string;
  readonly mode: MultiplayerMode;
  readonly visibility: LobbyVisibility;
  readonly hostPlayerId: string;
  readonly players: readonly LobbyPlayer[];
  readonly scenarioId: string | null;
  readonly difficulty: string | null;
  readonly status: "waiting" | "starting" | "in-match" | "closed";
  readonly revision: number;
}

export interface MatchSnapshot {
  readonly matchId: string;
  readonly revision: number;
  readonly serverTime: number;
  readonly mode: MultiplayerMode;
  readonly payload: Readonly<Record<string, unknown>>;
}

export type ClientCommandPayload =
  | {
      readonly type: "lobby:create";
      readonly mode: MultiplayerMode;
      readonly visibility: LobbyVisibility;
    }
  | { readonly type: "lobby:join"; readonly code: string }
  | { readonly type: "lobby:leave" }
  | { readonly type: "lobby:ready"; readonly ready: boolean }
  | { readonly type: "lobby:configure"; readonly scenarioId: string; readonly difficulty: string }
  | { readonly type: "match:start" }
  | { readonly type: "match:resume"; readonly matchId: string; readonly resumeToken: string }
  | { readonly type: "match:command"; readonly command: Readonly<Record<string, unknown>> }
  | { readonly type: "match:surrender" }
  | { readonly type: "ping"; readonly sentAt: number };

export interface ClientEnvelope {
  readonly protocolVersion: typeof DEFENSE_PROTOCOL_VERSION;
  readonly requestId: string;
  readonly clientSequence: number;
  readonly sentAt: number;
  readonly payload: ClientCommandPayload;
}

export type ServerEventPayload =
  | { readonly type: "session:welcome"; readonly playerId: string; readonly resumeToken?: string }
  | { readonly type: "lobby:state"; readonly lobby: LobbyState }
  | { readonly type: "lobby:closed"; readonly reason: string }
  | { readonly type: "match:snapshot"; readonly snapshot: MatchSnapshot }
  | {
      readonly type: "match:event";
      readonly revision: number;
      readonly event: Readonly<Record<string, unknown>>;
    }
  | {
      readonly type: "command:accepted";
      readonly requestId: string;
      readonly serverRevision: number;
    }
  | {
      readonly type: "command:rejected";
      readonly requestId: string;
      readonly code: string;
      readonly message: string;
    }
  | { readonly type: "pong"; readonly sentAt: number; readonly serverTime: number }
  | { readonly type: "error"; readonly code: string; readonly message: string };

export interface ServerEnvelope {
  readonly protocolVersion: number;
  readonly serverSequence: number;
  readonly receivedAt: number;
  readonly payload: ServerEventPayload;
}

export function isServerEnvelope(value: unknown): value is ServerEnvelope {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (typeof record.protocolVersion !== "number") return false;
  if (typeof record.serverSequence !== "number") return false;
  if (typeof record.receivedAt !== "number") return false;
  const payload = record.payload;
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return false;
  return typeof (payload as Record<string, unknown>).type === "string";
}

export function normalizeLobbyCode(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);
}
